<template>
  <div>
    <div class="d-flex align-center mb-6">
      <p class="text-2xl mb-0">Infos Marques</p>
      <v-spacer></v-spacer>
      <v-btn color="primary" class="mr-2" @click="openAddDialog">
        <v-icon left>{{ icons.mdiPlus }}</v-icon>
        Ajouter
      </v-btn>
    </div>

    <v-card>
      <v-card-title>
        <span>Liste des marques ({{ brandsTotal }})</span>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Rechercher"
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          @input="onSearchChange"
        ></v-text-field>
      </v-card-title>
      <v-card-text>
        <v-data-table
          dense
          :headers="headers"
          :items="brands"
          :loading="loading"
          :options.sync="options"
          :server-items-length="brandsTotal"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
          }"
        >
          <template slot="item.brand_name" slot-scope="{ item }">
            <strong>{{ item.brand_name }}</strong>
          </template>

          <template slot="item.baseline" slot-scope="{ item }">
            <span
              class="text-truncate d-inline-block"
              style="max-width: 250px"
              :title="item.baseline"
            >
              {{ item.baseline }}
            </span>
          </template>

          <template slot="item.title" slot-scope="{ item }">
            <span
              class="text-truncate d-inline-block"
              style="max-width: 250px"
              :title="item.title"
            >
              {{ item.title }}
            </span>
          </template>

          <template slot="item.template_number" slot-scope="{ item }">
            <v-chip
              v-if="item.template_number"
              x-small
              color="primary"
              text-color="white"
            >
              Template {{ item.template_number }}
            </v-chip>
            <span v-else class="grey--text">-</span>
          </template>

          <template slot="item.actions" slot-scope="{ item }">
            <v-btn icon small @click="openEditDialog(item)">
              <v-icon small color="primary">{{ icons.mdiPencil }}</v-icon>
            </v-btn>
            <v-btn icon small @click="confirmDelete(item)">
              <v-icon small color="error">{{ icons.mdiDelete }}</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="editDialog"
      max-width="800px"
      @click:outside="closeDialog"
    >
      <v-card>
        <v-card-title>
          {{ isEditing ? "Modifier la marque" : "Ajouter une marque" }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="brandForm">
            <v-row>
              <v-col cols="12">
                <h4 class="mb-2">Informations de base</h4>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.brand_name"
                  label="Nom de la marque *"
                  outlined
                  dense
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.author"
                  label="Auteur"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.brand_slug"
                  label="Slug"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.print_periodicity"
                  :items="periodicityOptions"
                  item-text="label"
                  item-value="value"
                  label="Périodicité print"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-tabs v-model="langTab" background-color="transparent" color="primary" grow>
                  <v-tab>Français</v-tab>
                  <v-tab>Anglais</v-tab>
                </v-tabs>

                <v-tabs-items v-model="langTab" class="pt-4">
                  <!-- French Tab -->
                  <v-tab-item>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.author_occupation"
                          label="Fonction de l'auteur (FR)"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.title"
                          label="Titre (FR)"
                          outlined
                          dense
                          rows="2"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.baseline"
                          label="Baseline (FR)"
                          outlined
                          dense
                          rows="2"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.positioning"
                          label="Positionnement (FR)"
                          outlined
                          dense
                          rows="3"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-tab-item>

                  <!-- English Tab -->
                  <v-tab-item>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.author_occupation_en"
                          label="Author Occupation (EN)"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.title_en"
                          label="Title (EN)"
                          outlined
                          dense
                          rows="2"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.baseline_en"
                          label="Baseline (EN)"
                          outlined
                          dense
                          rows="2"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.positioning_en"
                          label="Positioning (EN)"
                          outlined
                          dense
                          rows="3"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.template_number"
                  :items="templateNumberOptions"
                  item-text="label"
                  item-value="value"
                  label="Template"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-divider class="my-2"></v-divider>
                <h4 class="mb-3">Images</h4>
              </v-col>

              <v-col cols="12" md="4">
                <div class="image-upload-field">
                  <label class="text-caption grey--text">Logo</label>
                  <div
                    v-if="form.logo_path"
                    class="image-preview mb-2 checker-bg rounded"
                  >
                    <v-img
                      :src="getImageUrl(form.logo_path)"
                      max-height="120"
                      contain
                    ></v-img>
                    <v-btn
                      icon
                      x-small
                      class="remove-btn"
                      color="error"
                      @click="removeImage('logo')"
                    >
                      <v-icon x-small>{{ icons.mdiClose }}</v-icon>
                    </v-btn>
                  </div>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="uploading.logo"
                    :disabled="!form.id"
                    @click="$refs.logoInput.click()"
                  >
                    <v-icon left small>{{ icons.mdiUpload }}</v-icon>
                    {{ form.logo_path ? "Changer" : "Uploader" }}
                  </v-btn>
                  <input
                    ref="logoInput"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    style="display: none"
                    @change="onFileSelected($event, 'logos', 'logo_path')"
                  />
                  <div v-if="!form.id" class="text-caption grey--text mt-1">
                    Enregistrez d'abord la marque
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <div class="image-upload-field">
                  <label class="text-caption grey--text"
                    >Visuel présentation</label
                  >
                  <div
                    v-if="form.presentation_image_path"
                    class="image-preview mb-2 checker-bg rounded"
                  >
                    <v-img
                      :src="getImageUrl(form.presentation_image_path)"
                      max-height="120"
                      contain
                    ></v-img>
                    <v-btn
                      icon
                      x-small
                      class="remove-btn"
                      color="error"
                      @click="removeImage('presentation')"
                    >
                      <v-icon x-small>{{ icons.mdiClose }}</v-icon>
                    </v-btn>
                  </div>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="uploading.presentation"
                    :disabled="!form.id"
                    @click="$refs.presentationInput.click()"
                  >
                    <v-icon left small>{{ icons.mdiUpload }}</v-icon>
                    {{ form.presentation_image_path ? "Changer" : "Uploader" }}
                  </v-btn>
                  <input
                    ref="presentationInput"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    style="display: none"
                    @change="
                      onFileSelected(
                        $event,
                        'presentation',
                        'presentation_image_path'
                      )
                    "
                  />
                  <div v-if="!form.id" class="text-caption grey--text mt-1">
                    Enregistrez d'abord la marque
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <div class="image-upload-field">
                  <label class="text-caption grey--text">Visuel social</label>
                  <div
                    v-if="form.social_image_path"
                    class="image-preview mb-2 checker-bg rounded"
                  >
                    <v-img
                      :src="getImageUrl(form.social_image_path)"
                      max-height="120"
                      contain
                    ></v-img>
                    <v-btn
                      icon
                      x-small
                      class="remove-btn"
                      color="error"
                      @click="removeImage('social')"
                    >
                      <v-icon x-small>{{ icons.mdiClose }}</v-icon>
                    </v-btn>
                  </div>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="uploading.social"
                    :disabled="!form.id"
                    @click="$refs.socialInput.click()"
                  >
                    <v-icon left small>{{ icons.mdiUpload }}</v-icon>
                    {{ form.social_image_path ? "Changer" : "Uploader" }}
                  </v-btn>
                  <input
                    ref="socialInput"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    style="display: none"
                    @change="
                      onFileSelected($event, 'social', 'social_image_path')
                    "
                  />
                  <div v-if="!form.id" class="text-caption grey--text mt-1">
                    Enregistrez d'abord la marque
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <div class="image-upload-field">
                  <label class="text-caption grey--text">Image auteur</label>
                  <div
                    v-if="form.author_image_path"
                    class="image-preview mb-2 checker-bg rounded"
                  >
                    <v-img
                      :src="getImageUrl(form.author_image_path)"
                      max-height="120"
                      contain
                    ></v-img>
                    <v-btn
                      icon
                      x-small
                      class="remove-btn"
                      color="error"
                      @click="removeImage('author')"
                    >
                      <v-icon x-small>{{ icons.mdiClose }}</v-icon>
                    </v-btn>
                  </div>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="uploading.author"
                    :disabled="!form.id"
                    @click="$refs.authorInput.click()"
                  >
                    <v-icon left small>{{ icons.mdiUpload }}</v-icon>
                    {{ form.author_image_path ? "Changer" : "Uploader" }}
                  </v-btn>
                  <input
                    ref="authorInput"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    style="display: none"
                    @change="
                      onFileSelected($event, 'authors', 'author_image_path')
                    "
                  />
                  <div v-if="!form.id" class="text-caption grey--text mt-1">
                    Enregistrez d'abord la marque
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Annuler</v-btn>
          <v-btn color="primary" @click="saveBrand" :loading="saving">
            <v-icon left>{{ icons.mdiContentSave }}</v-icon>
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog"
      max-width="400px"
      @click:outside="deleteDialog = false"
    >
      <v-card>
        <v-card-title>Confirmer la suppression</v-card-title>
        <v-card-text>
          Voulez-vous vraiment supprimer la marque "{{
            brandToDelete ? brandToDelete.brand_name : ""
          }}" ?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Annuler</v-btn>
          <v-btn color="error" @click="deleteBrand" :loading="deleting"
            >Supprimer</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { NOTIF_DISPLAY_DURATION } from "@/utils/constants";
import {
  mdiClose,
  mdiContentSave,
  mdiDelete,
  mdiPencil,
  mdiPlus,
  mdiUpload,
} from "@mdi/js";

export default {
  name: "InfosMarques",
  data() {
    return {
      search: "",
      searchTimeout: null,
      options: {
        page: 1,
        itemsPerPage: 25,
      },
      headers: [
        { text: "Nom", value: "brand_name", width: "200px" },
        { text: "Baseline", value: "baseline" },
        { text: "Titre", value: "title" },
        { text: "Auteur", value: "author", width: "150px" },
        { text: "Template", value: "template_number", width: "120px" },
        { text: "Actions", value: "actions", sortable: false, width: "120px" },
      ],
      templateNumberOptions: [
        { value: 1, label: "Template 1" },
        { value: 2, label: "Template 2" },
        { value: 3, label: "Template 3" },
        { value: 4, label: "Template 4" },
      ],
      periodicityOptions: [
        { value: "quotidien", label: "Quotidien" },
        { value: "hebdomadaire", label: "Hebdomadaire" },
        { value: "bimensuel", label: "Bimensuel" },
        { value: "mensuel", label: "Mensuel" },
        { value: "bimestriel", label: "Bimestriel" },
        { value: "trimestriel", label: "Trimestriel" },
        { value: "semestriel", label: "Semestriel" },
        { value: "annuel", label: "Annuel" },
      ],
      editDialog: false,
      deleteDialog: false,
      isEditing: false,
      saving: false,
      deleting: false,
      brandToDelete: null,
      langTab: 0,
      form: {
        id: null,
        brand_name: "",
        brand_slug: "",
        baseline: "",
        baseline_en: "",
        title: "",
        title_en: "",
        positioning: "",
        positioning_en: "",
        author: "",
        author_occupation: "",
        author_occupation_en: "",
        logo_path: "",
        author_image_path: "",
        presentation_image_path: "",
        social_image_path: "",
        print_periodicity: "",
        template_number: null,
      },
      rules: {
        required: (v) => !!v || "Ce champ est requis",
      },
      icons: {
        mdiPlus,
        mdiPencil,
        mdiDelete,
        mdiContentSave,
        mdiUpload,
        mdiClose,
      },
      uploading: {
        logo: false,
        author: false,
        presentation: false,
        social: false,
      },
      imageCacheBuster: Date.now(),
    };
  },
  computed: {
    brands() {
      return this.$store.getters["marketing/getBrands"];
    },
    brandsTotal() {
      return this.$store.getters["marketing/getBrandsTotal"];
    },
    loading() {
      return this.$store.getters["marketing/getLoading"];
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchBrands();
      },
      deep: true,
    },
  },
  created() {
    this.fetchBrands();
  },
  methods: {
    async fetchBrands() {
      const { page, itemsPerPage } = this.options;
      const params = {
        page,
        limit: itemsPerPage,
      };
      if (this.search) {
        params.search = this.search;
      }
      await this.$store.dispatch("marketing/fetchBrands", params);
    },
    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1;
        this.fetchBrands();
      }, 500);
    },
    openAddDialog() {
      this.isEditing = false;
      this.langTab = 0;
      this.form = {
        id: null,
        brand_name: "",
        brand_slug: "",
        baseline: "",
        baseline_en: "",
        title: "",
        title_en: "",
        positioning: "",
        positioning_en: "",
        author: "",
        author_occupation: "",
        author_occupation_en: "",
        logo_path: "",
        author_image_path: "",
        presentation_image_path: "",
        social_image_path: "",
        print_periodicity: "",
        template_number: null,
      };
      this.editDialog = true;
    },
    openEditDialog(brand) {
      this.isEditing = true;
      this.langTab = 0;
      this.form = {
        id: brand.id,
        brand_name: brand.brand_name,
        brand_slug: brand.brand_slug || "",
        baseline: brand.baseline || "",
        baseline_en: brand.baseline_en || "",
        title: brand.title || "",
        title_en: brand.title_en || "",
        positioning: brand.positioning || "",
        positioning_en: brand.positioning_en || "",
        author: brand.author || "",
        author_occupation: brand.author_occupation || "",
        author_occupation_en: brand.author_occupation_en || "",
        logo_path: brand.logo_path || "",
        author_image_path: brand.author_image_path || "",
        presentation_image_path: brand.presentation_image_path || "",
        social_image_path: brand.social_image_path || "",
        print_periodicity: brand.print_periodicity || "",
        template_number: brand.template_number || null,
      };
      this.editDialog = true;
    },
    closeDialog() {
      this.editDialog = false;
      if (this.$refs.brandForm) {
        this.$refs.brandForm.resetValidation();
      }
    },
    async saveBrand() {
      if (!this.$refs.brandForm.validate()) {
        return;
      }

      this.saving = true;
      try {
        const brandData = {
          brand_name: this.form.brand_name,
          brand_slug: this.form.brand_slug || null,
          baseline: this.form.baseline || null,
          baseline_en: this.form.baseline_en || null,
          title: this.form.title || null,
          title_en: this.form.title_en || null,
          positioning: this.form.positioning || null,
          positioning_en: this.form.positioning_en || null,
          author: this.form.author || null,
          author_occupation: this.form.author_occupation || null,
          author_occupation_en: this.form.author_occupation_en || null,
          logo_path: this.form.logo_path || null,
          author_image_path: this.form.author_image_path || null,
          presentation_image_path: this.form.presentation_image_path || null,
          social_image_path: this.form.social_image_path || null,
          print_periodicity: this.form.print_periodicity || null,
          template_number: this.form.template_number,
        };

        if (this.isEditing) {
          await this.$store.dispatch("marketing/updateBrand", {
            brandId: this.form.id,
            brandData,
          });
          this.showNotification("Marque modifiée avec succès", "success");
        } else {
          await this.$store.dispatch("marketing/createBrand", brandData);
          this.showNotification("Marque créée avec succès", "success");
        }

        this.closeDialog();
        this.fetchBrands();
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.detail
            ? error.response.data.detail
            : "Une erreur est survenue";
        this.showNotification(message, "error");
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(brand) {
      this.brandToDelete = brand;
      this.deleteDialog = true;
    },
    async deleteBrand() {
      if (!this.brandToDelete) return;

      this.deleting = true;
      try {
        await this.$store.dispatch(
          "marketing/deleteBrand",
          this.brandToDelete.id
        );
        this.showNotification("Marque supprimée avec succès", "success");
        this.deleteDialog = false;
        this.brandToDelete = null;
        this.fetchBrands();
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.detail
            ? error.response.data.detail
            : "Une erreur est survenue";
        this.showNotification(message, "error");
      } finally {
        this.deleting = false;
      }
    },
    showNotification(text, color) {
      this.$store.dispatch("app/setSnackBar", {
        show: true,
        text,
        color,
        timeout: NOTIF_DISPLAY_DURATION,
      });
    },
    getImageUrl(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;
      var baseUrl = process.env.VUE_APP_API_URL || "";
      if (baseUrl.endsWith("/")) {
        baseUrl = baseUrl.slice(0, -1);
      }
      return baseUrl + "/uploads/" + path + "?v=" + this.imageCacheBuster;
    },
    async onFileSelected(event, folder, formField) {
      const file = event.target.files[0];
      if (!file) return;

      if (!this.form.id) {
        this.showNotification(
          "Veuillez d'abord enregistrer la marque avant d'uploader des images",
          "warning"
        );
        event.target.value = "";
        return;
      }

      var uploadKey = "logo";
      if (folder === "logos") {
        uploadKey = "logo";
      } else if (folder === "authors") {
        uploadKey = "author";
      } else if (folder === "presentation") {
        uploadKey = "presentation";
      } else if (folder === "social") {
        uploadKey = "social";
      }
      this.uploading[uploadKey] = true;

      try {
        const result = await this.$store.dispatch("marketing/uploadImage", {
          folder,
          file,
          filename: String(this.form.id),
          oldPath: this.form[formField] || null,
        });
        this.form[formField] = result.path;
        this.imageCacheBuster = Date.now();

        var updateData = {};
        updateData[formField] = result.path;
        await this.$store.dispatch("marketing/updateBrand", {
          brandId: this.form.id,
          brandData: updateData,
        });
        this.fetchBrands();

        this.showNotification("Image uploadée avec succès", "success");
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.detail
            ? error.response.data.detail
            : "Erreur lors de l'upload";
        this.showNotification(message, "error");
      } finally {
        this.uploading[uploadKey] = false;
        event.target.value = "";
      }
    },
    async removeImage(type) {
      var path = null;
      var formField = null;

      if (type === "logo") {
        path = this.form.logo_path;
        formField = "logo_path";
      } else if (type === "author") {
        path = this.form.author_image_path;
        formField = "author_image_path";
      } else if (type === "presentation") {
        path = this.form.presentation_image_path;
        formField = "presentation_image_path";
      } else if (type === "social") {
        path = this.form.social_image_path;
        formField = "social_image_path";
      }

      if (path && formField) {
        var parts = path.split("/");
        if (parts.length >= 3) {
          var folder = parts[1];
          var filename = parts[2];
          try {
            await this.$store.dispatch("marketing/deleteImage", {
              folder: folder,
              filename: filename,
            });
          } catch (error) {
            // Ignore deletion errors (file might not exist)
          }
        }
        this.form[formField] = null;

        if (this.form.id) {
          var updateData = {};
          updateData[formField] = null;
          await this.$store.dispatch("marketing/updateBrand", {
            brandId: this.form.id,
            brandData: updateData,
          });
          this.fetchBrands();
        }
      }
    },
  },
};
</script>

<style scoped>
.search-field {
  max-width: 300px;
}

.image-upload-field {
  display: flex;
  flex-direction: column;
}

.image-preview {
  position: relative;
  display: inline-block;
}

.image-preview .remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: white;
}

.checker-bg {
  background-color: #fff;
  background-image: linear-gradient(
      45deg,
      #ccc 25%,
      transparent 25%,
      transparent 75%,
      #ccc 75%,
      #ccc
    ),
    linear-gradient(
      45deg,
      #ccc 25%,
      transparent 25%,
      transparent 75%,
      #ccc 75%,
      #ccc
    );
  background-size: 20px 20px;
  background-position: 0 0, 10px 10px;
  background-repeat: repeat;
}

.checker-bg >>> .v-image__image {
  background-color: transparent !important;
}
</style>
